import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
    Box, Typography, TextField, IconButton, Avatar, MenuItem, Menu,
    Alert, InputAdornment, Select, Card, Skeleton, Tooltip,
    Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
} from "@mui/material";
import SearchIcon             from "@mui/icons-material/Search";
import ClearIcon              from "@mui/icons-material/Clear";
import PersonRoundedIcon      from "@mui/icons-material/PersonRounded";
import PhoneRoundedIcon       from "@mui/icons-material/PhoneRounded";
import BadgeRoundedIcon       from "@mui/icons-material/BadgeRounded";
import OpenInNewIcon          from "@mui/icons-material/OpenInNew";
import MoreVertIcon           from "@mui/icons-material/MoreVert";
import KeyboardArrowLeft      from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight     from "@mui/icons-material/KeyboardArrowRight";
import { getData, postData, API_ENDPOINTS } from "../../services/apiService.js";
import { encryptId } from "../helper/idCodec.js";
import { useNavigate } from "react-router-dom";

// ── Brand palette ─────────────────────────────────────────────────────────
const T = {
    teal:    "#007C84", tealDk: "#016268", tealLt: "#E0F4F5",
    orange:  "#FF7900", orangeLt: "#FFF0E0",
    emerald: "#059669", emeraldLt: "#D1FAE5",
    rose:    "#E11D48", roseLt:   "#FFE4E6",
    indigo:  "#4F46E5", indigoLt: "#EEF2FF",
    amber:   "#D97706", amberLt:  "#FEF3C7",
    bg:      "#F7FAFC", border: "#E2E8F0",
    text:    "#0F172A", muted:  "#64748B", surface: "#FFFFFF",
};

// Avatar background per initial
const AVATAR_PALETTE = [
    [T.tealLt, T.teal], [T.orangeLt, T.orange], [T.indigoLt, T.indigo],
    [T.emeraldLt, T.emerald], [T.amberLt, T.amber], [T.roseLt, T.rose],
];
const avatarBg  = (name = "") => AVATAR_PALETTE[name.charCodeAt(0) % AVATAR_PALETTE.length];
const initials  = (name = "") => { const p = name.trim().split(" "); return p.length >= 2 ? (p[0][0]+p[1][0]).toUpperCase() : (p[0]?.[0] ?? "?").toUpperCase(); };

// ── Status chip ───────────────────────────────────────────────────────────
const DutyChip = ({ on }) => (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.6, px: 1.2, py: 0.4, borderRadius: "8px",
        bgcolor: on ? T.emeraldLt : T.roseLt, border: `1px solid ${on ? T.emerald : T.rose}22` }}>
        <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: on ? T.emerald : T.rose }} />
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: on ? T.emerald : T.rose, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            {on ? "On Duty" : "Off Duty"}
        </Typography>
    </Box>
);

// ── Filter pill ───────────────────────────────────────────────────────────
const FilterPill = ({ label, active, color, bg, onClick }) => (
    <Box onClick={onClick} sx={{
        px: 1.6, py: 0.6, borderRadius: "8px", cursor: "pointer",
        bgcolor: active ? bg : "transparent", color: active ? color : T.muted,
        fontWeight: active ? 700 : 500, fontSize: 12.5,
        border: `1.5px solid ${active ? color + "44" : T.border}`,
        transition: "all 0.14s", whiteSpace: "nowrap",
        "&:hover": { bgcolor: bg, color },
        fontFamily: "'Plus Jakarta Sans', sans-serif",
    }}>{label}</Box>
);

// ── Skeleton row ──────────────────────────────────────────────────────────
const SkelRow = () => (
    <TableRow>
        {[180, 80, 120, 100, 120, 40].map((w, i) => (
            <TableCell key={i}><Skeleton width={w} height={18} /></TableCell>
        ))}
    </TableRow>
);

// ── Main component ────────────────────────────────────────────────────────
const STATUS_FILTERS = [
    { label: "All",      value: "all",     color: T.teal,    bg: T.tealLt    },
    { label: "On Duty",  value: "onDuty",  color: T.emerald, bg: T.emeraldLt },
    { label: "Off Duty", value: "offDuty", color: T.rose,    bg: T.roseLt    },
];

const AllDrivers = () => {
    const [drivers,      setDrivers]      = useState([]);
    const [search,       setSearch]       = useState("");
    const [searchApplied,setSearchApplied]= useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [page,         setPage]         = useState(0);
    const [perPage,      setPerPage]      = useState(15);
    const [loading,      setLoading]      = useState(true);
    const [error,        setError]        = useState(null);
    const [menuAnchor,   setMenuAnchor]   = useState(null);
    const [menuDriver,   setMenuDriver]   = useState(null);
    const navigate = useNavigate();

    const fetchDrivers = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const params = { page: page + 1, limit: perPage };
            if (searchApplied) params.search = searchApplied;
            if (statusFilter === "onDuty")  params.isOnDuty = true;
            if (statusFilter === "offDuty") params.isOnDuty = false;

            const res = await getData(API_ENDPOINTS.getDriversList, params);
            setDrivers((res.result || []).map(d => ({
                id:        d.driver_id,
                name:      `${d.first_name} ${d.last_name}`.trim(),
                email:     d.email,
                avatar:    d.pic || undefined,
                isOnDuty:  d.isOnDuty,
                mobile:    d.mobile_num,
                licenseNo: d.license_no,
                rating:    d.rating ?? 0,
            })));
        } catch (e) {
            setError(e.message || "Failed to fetch drivers.");
        } finally {
            setLoading(false);
        }
    }, [page, perPage, searchApplied, statusFilter]);

    useEffect(() => { fetchDrivers(); }, [fetchDrivers]);

    const onDutyCount  = drivers.filter(d => d.isOnDuty).length;
    const offDutyCount = drivers.length - onDutyCount;

    // Pagination (client-side on current fetch)
    const totalPages = Math.ceil(drivers.length / perPage);
    const paginated  = drivers.slice(page * perPage, (page + 1) * perPage);

    const handleSearch   = e => { if (e.key === "Enter") { setSearchApplied(search); setPage(0); } };
    const handleClear    = () => { setSearch(""); setSearchApplied(""); setPage(0); };
    const handleFilter   = v => { setStatusFilter(v); setPage(0); };
    const handleView     = id => navigate(`/app/driver-details/${encryptId(id)}`);

    const handleToggleDuty = useCallback(async (id, current) => {
        setDrivers(prev => prev.map(d => d.id === id ? { ...d, isOnDuty: !current } : d));
        setMenuAnchor(null); setMenuDriver(null);
        try { await postData(API_ENDPOINTS.markOnDuty, { driver_id: id, isOnDuty: !current }); }
        catch (_) { setDrivers(prev => prev.map(d => d.id === id ? { ...d, isOnDuty: current } : d)); }
    }, []);

    return (
        <Box>
            {/* ── Summary pills ── */}
            {!loading && drivers.length > 0 && (
                <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 2.5 }}>
                    {[
                        { label: "Total",    value: drivers.length, color: T.teal,   bg: T.tealLt    },
                        { label: "On Duty",  value: onDutyCount,   color: T.emerald,bg: T.emeraldLt },
                        { label: "Off Duty", value: offDutyCount,  color: T.rose,   bg: T.roseLt    },
                    ].map(s => (
                        <Box key={s.label} sx={{ display: "flex", alignItems: "center", gap: 0.8, bgcolor: s.bg, border: `1px solid ${s.color}22`, borderRadius: "10px", px: 1.5, py: 0.7 }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: s.color }} />
                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: s.color }}>{s.label}: {s.value}</Typography>
                        </Box>
                    ))}
                </Box>
            )}

            {/* ── Toolbar ── */}
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center", mb: 2.5 }}>
                <TextField
                    placeholder="Search by name or ID…"
                    size="small" value={search}
                    onChange={e => setSearch(e.target.value)}
                    onKeyDown={handleSearch}
                    sx={{ flex: "1 1 240px", maxWidth: 360 }}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: T.muted }} /></InputAdornment>,
                        endAdornment: search ? <InputAdornment position="end"><IconButton size="small" onClick={handleClear}><ClearIcon sx={{ fontSize: 16 }} /></IconButton></InputAdornment> : null,
                        sx: { borderRadius: "12px", bgcolor: T.surface, fontSize: 13 },
                    }}
                />

                {/* Status filter pills */}
                <Box sx={{ display: "flex", gap: 0.75, bgcolor: T.surface, border: `1px solid ${T.border}`, borderRadius: "12px", p: 0.5 }}>
                    {STATUS_FILTERS.map(f => (
                        <FilterPill key={f.value} label={f.label} active={statusFilter === f.value} color={f.color} bg={f.bg} onClick={() => handleFilter(f.value)} />
                    ))}
                </Box>

                {!loading && <Typography sx={{ fontSize: 13, color: T.muted, ml: "auto" }}>{drivers.length} driver{drivers.length !== 1 ? "s" : ""}</Typography>}
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2, borderRadius: "12px" }}>{error}</Alert>}

            {/* ── Table ── */}
            <Card sx={{ borderRadius: "16px", border: `1px solid ${T.border}`, boxShadow: "0 1px 4px rgba(15,23,42,0.06)", overflow: "hidden" }}>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: T.bg }}>
                                {["Driver", "ID", "License No.", "Mobile", "Status", ""].map(h => (
                                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: 11, color: T.muted, textTransform: "uppercase", letterSpacing: "0.4px", py: 1.5, borderBottom: `2px solid ${T.border}` }}>
                                        {h}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading
                                ? Array.from({ length: 10 }).map((_, i) => <SkelRow key={i} />)
                                : paginated.length === 0
                                ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                                            <PersonRoundedIcon sx={{ fontSize: 40, color: T.border, mb: 1, display: "block", mx: "auto" }} />
                                            <Typography fontSize={13} color="text.secondary">No drivers found.</Typography>
                                        </TableCell>
                                    </TableRow>
                                )
                                : paginated.map(d => {
                                    const [bg, fg] = avatarBg(d.name);
                                    return (
                                        <TableRow key={d.id} hover
                                            sx={{ cursor: "pointer", "&:last-child td": { border: 0 }, "&:hover": { bgcolor: T.tealLt + "44" } }}
                                            onClick={() => handleView(d.id)}
                                        >
                                            {/* Name + avatar */}
                                            <TableCell>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                                    <Avatar src={d.avatar} sx={{ width: 36, height: 36, bgcolor: bg, color: fg, fontSize: 13, fontWeight: 800 }}>
                                                        {!d.avatar && initials(d.name)}
                                                    </Avatar>
                                                    <Box>
                                                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: T.text }}>{d.name}</Typography>
                                                        {d.email && <Typography sx={{ fontSize: 11, color: T.muted }}>{d.email}</Typography>}
                                                    </Box>
                                                </Box>
                                            </TableCell>

                                            {/* ID */}
                                            <TableCell>
                                                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, bgcolor: T.bg, border: `1px solid ${T.border}`, borderRadius: "7px", px: 1, py: 0.3 }}>
                                                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: T.muted, fontFamily: "monospace" }}>#{d.id}</Typography>
                                                </Box>
                                            </TableCell>

                                            {/* License */}
                                            <TableCell>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.7 }}>
                                                    <BadgeRoundedIcon sx={{ fontSize: 13, color: T.muted }} />
                                                    <Typography sx={{ fontSize: 12, color: T.text, fontWeight: 500 }}>{d.licenseNo || "—"}</Typography>
                                                </Box>
                                            </TableCell>

                                            {/* Mobile */}
                                            <TableCell>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.7 }}>
                                                    <PhoneRoundedIcon sx={{ fontSize: 13, color: T.muted }} />
                                                    <Typography sx={{ fontSize: 12, color: T.text }}>{d.mobile || "—"}</Typography>
                                                </Box>
                                            </TableCell>

                                            {/* Status */}
                                            <TableCell><DutyChip on={d.isOnDuty} /></TableCell>

                                            {/* Actions */}
                                            <TableCell padding="none">
                                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                                    <Tooltip title="View profile">
                                                        <IconButton size="small" onClick={e => { e.stopPropagation(); handleView(d.id); }} sx={{ color: T.muted }}>
                                                            <OpenInNewIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <IconButton size="small" onClick={e => { e.stopPropagation(); setMenuAnchor(e.currentTarget); setMenuDriver(d); }}>
                                                        <MoreVertIcon sx={{ fontSize: 18, color: T.muted }} />
                                                    </IconButton>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            }
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Context menu */}
                <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => { setMenuAnchor(null); setMenuDriver(null); }}
                    PaperProps={{ sx: { borderRadius: "12px", border: `1px solid ${T.border}`, boxShadow: "0 4px 16px rgba(0,0,0,0.1)" } }}>
                    <MenuItem onClick={() => { handleView(menuDriver?.id); setMenuAnchor(null); setMenuDriver(null); }} sx={{ fontSize: 13 }}>
                        View Profile
                    </MenuItem>
                    <MenuItem onClick={() => handleToggleDuty(menuDriver?.id, menuDriver?.isOnDuty)} sx={{ fontSize: 13, color: menuDriver?.isOnDuty ? T.rose : T.emerald, fontWeight: 600 }}>
                        {menuDriver?.isOnDuty ? "Mark Off Duty" : "Mark On Duty"}
                    </MenuItem>
                </Menu>

                {/* Pagination */}
                {!loading && drivers.length > 0 && (
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 2.5, py: 1.5, borderTop: `1px solid ${T.border}` }}>
                        <Typography sx={{ fontSize: 13, color: T.muted }}>
                            Showing {Math.min(page * perPage + 1, drivers.length)}–{Math.min((page + 1) * perPage, drivers.length)} of {drivers.length}
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Select value={perPage} onChange={e => { setPerPage(Number(e.target.value)); setPage(0); }} size="small"
                                sx={{ borderRadius: "10px", fontSize: 13 }}>
                                {[15, 25, 50].map(n => <MenuItem key={n} value={n}>{n} per page</MenuItem>)}
                            </Select>
                            <IconButton size="small" onClick={() => setPage(p => Math.max(p-1, 0))} disabled={page === 0}
                                sx={{ bgcolor: T.bg, border: `1px solid ${T.border}`, borderRadius: "8px" }}>
                                <KeyboardArrowLeft sx={{ fontSize: 18 }} />
                            </IconButton>
                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.text, minWidth: 56, textAlign: "center" }}>
                                {page + 1} / {totalPages}
                            </Typography>
                            <IconButton size="small" onClick={() => setPage(p => Math.min(p+1, totalPages-1))} disabled={page >= totalPages-1}
                                sx={{ bgcolor: T.bg, border: `1px solid ${T.border}`, borderRadius: "8px" }}>
                                <KeyboardArrowRight sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Box>
                    </Box>
                )}
            </Card>
        </Box>
    );
};

export default AllDrivers;
